import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Form, Input, Space, Tag, Typography, message } from 'antd';
import { getQiniuConfig, updateQiniuConfig, type AdminQiniuConfig } from '../services/api';

const defaultUploadURL = 'https://up-z1.qiniup.com';

const QiniuConfig: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasSecret, setHasSecret] = useState(false);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getQiniuConfig();
      if (res.code !== 0) {
        message.error(res.message || '加载七牛配置失败');
        return;
      }
      const config = res.data as AdminQiniuConfig;
      setHasSecret(Boolean(config.has_secret_key));
      form.setFieldsValue({
        access_key: config.access_key || '',
        secret_key: '',
        bucket: config.bucket || '',
        domain: config.domain || '',
        upload_url: config.upload_url || defaultUploadURL,
      });
    } catch (error) {
      message.error('加载七牛配置失败');
    } finally {
      setLoading(false);
    }
  }, [form]);

  useEffect(() => {
    void loadConfig();
  }, [loadConfig]);

  const onFinish = async (values: {
    access_key: string;
    secret_key?: string;
    bucket: string;
    domain: string;
    upload_url?: string;
  }) => {
    setSaving(true);
    try {
      const payload = {
        access_key: (values.access_key || '').trim(),
        secret_key: (values.secret_key || '').trim(),
        bucket: (values.bucket || '').trim(),
        domain: (values.domain || '').trim(),
        upload_url: (values.upload_url || '').trim() || defaultUploadURL,
      };

      const res = await updateQiniuConfig(payload);
      if (res.code !== 0) {
        message.error(res.message || '保存七牛配置失败');
        return;
      }

      message.success('七牛配置已保存');
      setHasSecret(Boolean(res.data?.has_secret_key));
      form.setFieldsValue({
        access_key: res.data?.access_key || payload.access_key,
        secret_key: '',
        bucket: res.data?.bucket || payload.bucket,
        domain: res.data?.domain || payload.domain,
        upload_url: res.data?.upload_url || payload.upload_url,
      });
    } catch (error) {
      message.error('保存七牛配置失败');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Typography.Title level={3} style={{ marginTop: 0 }}>
        七牛上传配置
      </Typography.Title>

      <Card loading={loading}>
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Alert
            type="info"
            showIcon
            message="保存后后台上传组件会立即使用新配置，无需重启服务。"
          />

          <div>
            当前密钥状态：
            {hasSecret ? <Tag color="green">已配置 SecretKey</Tag> : <Tag color="red">未配置 SecretKey</Tag>}
          </div>

          <Form form={form} layout="vertical" onFinish={onFinish}>
            <Form.Item
              label="AccessKey"
              name="access_key"
              rules={[{ required: true, message: '请输入 AccessKey' }]}
            >
              <Input placeholder="请输入七牛 AccessKey" />
            </Form.Item>

            <Form.Item
              label="SecretKey"
              name="secret_key"
              extra="留空表示保留当前密钥，不会覆盖。"
            >
              <Input.Password placeholder="请输入新的 SecretKey（可留空）" />
            </Form.Item>

            <Form.Item
              label="空间名称（Bucket）"
              name="bucket"
              rules={[{ required: true, message: '请输入 Bucket' }]}
            >
              <Input placeholder="请输入 Bucket" />
            </Form.Item>

            <Form.Item
              label="访问域名（Domain）"
              name="domain"
              rules={[{ required: true, message: '请输入 Domain' }]}
              extra="示例：https://store.example.com"
            >
              <Input placeholder="请输入访问域名" />
            </Form.Item>

            <Form.Item
              label="上传地址（UploadURL）"
              name="upload_url"
              rules={[{ required: true, message: '请输入上传地址' }]}
            >
              <Input placeholder={defaultUploadURL} />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0 }}>
              <Button type="primary" htmlType="submit" loading={saving}>
                保存配置
              </Button>
            </Form.Item>
          </Form>
        </Space>
      </Card>
    </div>
  );
};

export default QiniuConfig;
